/* eslint @typescript-eslint/no-extra-semi: "off" */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Remarkable } from 'remarkable'
import { graphql } from 'gatsby'
import { isEqual, orderBy } from 'lodash-es'
import GoogleMapReact from 'google-map-react'

// Component imports
import Layout from 'src/components/core/community/layout'
import MetaData from 'src/components/core/metaData'
import SpeakersList from 'src/components/speakersList'
import Testimonials from 'src/components/testimonials'
import PartnersList from 'src/components/partnersList'
import Tickets from 'src/components/tickets'
import AgendaTemplate from 'theme/components/organisms/agenda/agenda-template'
import CommunityLeaders from 'src/components/communityLeaders.js'
import NewsletterSignUpSection from 'src/components/newsletterSignUpSection.js'
import WatchLiveAnnouncement from 'src/components/watchLiveAnnouncement'

// Utilities and services
import { useTimezoneService } from 'theme/services/timezoneService'
import { startAndEndTime, startAndEndDate } from 'src/utils/date-helpers'
import { calculateConferenceStatus } from 'src/utils/session-helpers'
import { getEventLink, getConferenceLocation } from 'theme/utils/generic-helpers'
import { getSchemaData, getVenueGeoLocation } from 'src/utils/meta-helpers'

function CommunityPage({ data }) {
  const timezoneService = useTimezoneService()
  const md = new Remarkable({ breaks: true })

  // State declarations
  const [conferenceStatus, setConferenceStatus] = useState({ started: false, ended: false })
  const [isLoaded, setIsLoaded] = useState(false)
  const [conferenceLinks, setConferenceLinks] = useState({
    tickets: false,
    externalTickets: false,
    speakers: false,
    partners: false,
  })
  const [onDisplayTickets, setOnDisplayTickets] = useState([])
  const [schemaData, setSchemaData] = useState([])

  // Initial data processing
  const conference = data.conference
  const stageDays = conference.data.Stage_Days
  conference.data.Sessions = data.sessions.nodes
  const conferenceLocation = getConferenceLocation(conference)
  const venue = conference.data.Venue && conference.data.Venue[0]
  const inPerson = venue.data.Venue_Type === 'In-Person' ? venue : null

  // Calculate map data
  const mapCenter = inPerson &&
    !(!inPerson.data.Venue_Latitude || !inPerson.data.Venue_Longitude) && {
      lat: parseFloat(inPerson.data.Venue_Latitude, 10),
      lng: parseFloat(inPerson.data.Venue_Longitude, 10),
    }

  // Process speakers data
  const speakers = data.speakers.nodes.filter((speaker) => {
    const speakerSessions = speaker.data.Sessions
    if (speakerSessions) {
      const speakerSession = speakerSessions.filter(
        (session) =>
          session.data.Conference?.[0] &&
          session.data.Conference[0].data.Conference_Id === conference.data.Conference_Id &&
          (session.data.Session_Status === 'Confirmed' || session.data.Session_Status === 'Published'),
      )
      return speakerSession.length > 0
    }
    return false
  })

  // Process partners data
  const partners = conference.data.Partners
    ? conference.data.Partners.sort((a, b) => a.data.Company_Priority - b.data.Company_Priority)
    : []

  const shuffledSpeakers = speakers.filter(
    (speaker) =>
      speaker?.data?.Person &&
      speaker.data.Person[0].data.Person_Status === 'Published' &&
      !speaker.data.Person[0].data.Hide_Speaker,
  )

  // Ticket processing functions
  const getOnDisplayTickets = () => {
    const today = new Date()
    const unsortedTickets = [...data.tickets2.nodes] // Create new array reference
      .map((ticket) => {
        const price = ticket.data.Prices.filter(
          (price) =>
            today > new Date(price.data.UTC_Display_Time) && today < new Date(price.data.UTC_End_Time),
        )[0]

        if (!price) return null

        return {
          id: ticket.id,
          recordId: ticket.recordId,
          data: {
            ...ticket.data,
            ...price.data,
            Currency: price.data.Currency && price.data.Currency[0],
          },
        }
      })
      .filter(Boolean)

    return orderBy(unsortedTickets, 'data.Priority')
  }

  // Calculate conference times
  let startTime, endTime
  data.sessions.nodes.forEach((session) => {
    if (
      session.data.Session_Groups &&
      session.data.Session_Groups[0] &&
      session.data.Session_Groups[0].data.Delivery === 'Live'
    ) {
      if (!startTime || startTime > session.data.UTC_Start_Time) startTime = session.data.UTC_Start_Time
      if (!endTime || endTime < session.data.UTC_End_Time) endTime = session.data.UTC_End_Time
    }
  })

  // Effects for state management
  useEffect(() => {
    const status = calculateConferenceStatus(conference.data.Sessions)
    setConferenceStatus(status)
    setIsLoaded(true)

    const timer = setInterval(() => {
      const newStatus = calculateConferenceStatus(conference.data.Sessions)
      if (!isEqual(newStatus, status)) {
        setConferenceStatus(newStatus)
      }
    }, 1000 * 60)

    return () => clearInterval(timer)
  }, [conference.data.Sessions])

  useEffect(() => {
    const displayTickets = getOnDisplayTickets()
    setOnDisplayTickets(displayTickets)

    const timer = setInterval(() => {
      const newDisplayTickets = getOnDisplayTickets()
      if (!isEqual(newDisplayTickets, displayTickets)) {
        setOnDisplayTickets(newDisplayTickets)
      }
    }, 1000 * 60)

    return () => clearInterval(timer)
  }, [data.tickets2.nodes])

  // Combined effect for conferenceLinks
  useEffect(() => {
    if (onDisplayTickets && conferenceStatus) {
      setConferenceLinks({
        tickets: !conferenceStatus.ended && onDisplayTickets.length > 0,
        externalTickets: conference.data.External_Hosting_Site,
        speakers: false,
        partners: conference.data.Days_Away > -30,
      })
    }
  }, [onDisplayTickets, conferenceStatus, conference.data])

  // Schema data effect
  useEffect(() => {
    const metaDescription =
      'A local meetup that brings together the latest practices in Product, UX, Design and Dev for high-performing cross-functional product teams.'
    setSchemaData(getSchemaData(conference, onDisplayTickets, conference.data.Venue, metaDescription))
  }, [onDisplayTickets, conference])

  const conferenceTimes = startAndEndTime(
    startTime,
    endTime,
    inPerson ? conference.data.Timezone : timezoneService?.timezone,
  )

  // Map marker renderer
  const renderMarkers = (map, maps, mapCenter, venueName) => {
    new maps.Marker({
      position: mapCenter,
      map,
      title: venueName,
    })
  }

  return (
    <Layout conference={conference} conferenceLinks={conferenceLinks} inPerson={inPerson}>
      <MetaData
        keywords={[
          'uxdx',
          'product',
          'ux',
          'design',
          'dev',
          'agile',
          'digital transformation',
          'conference',
          'meetup',
        ]}
        title={
          conference.data.Conference_Name +
          ' | ' +
          startAndEndDate(startTime, endTime, inPerson ? conference.data.Timezone : timezoneService.timezone)
        }
        description="A local meetup that brings together the latest practices in Product, UX, Design and Dev for high-performing cross-functional product teams."
        image={conference.data.Manual_Conference_Card_Url || conference.data.Meta_Community_Url}
        geo={getVenueGeoLocation(conference.data.Venue)}
      >
        <script type="application/ld+json">{`${JSON.stringify(schemaData)}`}</script>
      </MetaData>

      <section className="mx-auto">
        <div className="relative bg-white overflow-hidden">
          <div className="max-w-screen-xl mx-auto">
            <div className="relative z-10 pb-8 bg-white sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
              <svg
                className="hidden lg:block absolute right-0 inset-y-0 h-full w-48 text-white transform translate-x-1/2"
                fill="currentColor"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                <polygon points="50,0 100,0 50,100 0,100" />
              </svg>

              <div className="mx-auto max-w-xl px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 lg:pt-28">
                <div className="sm:text-center lg:text-left">
                  <h1 className="text-4xl tracking-tight leading-10 font-extrabold text-gray-900 sm:text-5xl sm:leading-none md:text-6xl">
                    {conference.data.Conference_Name}
                  </h1>
                  <p className="text-community-600 text-2xl mt-4 font-bold">
                    {startAndEndDate(
                      startTime,
                      endTime,
                      inPerson ? conference.data.Timezone : timezoneService.timezone,
                    )}
                    <span className="text-gray-800 ml-2 font-normal">{conferenceTimes}</span>
                    <span className="text-gray-600 ml-2 text-sm font-normal">
                      {inPerson ? conference.data.Timezone : timezoneService.timezone}
                    </span>
                  </p>
                  <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                    {conference.data.Sales_Blurb}
                  </p>
                  {onDisplayTickets.length > 0 && (
                    <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                      <div className="rounded-md shadow">
                        <a
                          href="#tickets"
                          className="bg-community-600 hover:bg-community-500 focus:border-community-700 focus:outline-none w-full flex items-center justify-center px-8 py-3 border border-transparent text-base leading-6 font-medium rounded-md text-white focus:ring-community transition duration-150 ease-in-out md:py-4 md:text-lg md:px-10"
                        >
                          Register Now
                        </a>
                      </div>
                    </div>
                  )}
                  {conference.data.External_Hosting_Site && (
                    <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                      <div className="rounded-md shadow">
                        <a
                          href={conference.data.External_Hosting_Site}
                          className="bg-community-600 hover:bg-community-500 focus:border-community-700 focus:outline-none w-full flex items-center justify-center px-8 py-3 border border-transparent text-base leading-6 font-medium rounded-md text-white focus:ring-community transition duration-150 ease-in-out md:py-4 md:text-lg md:px-10"
                        >
                          Register Now
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
            {/* <GatsbyImage
              image={headerImage}
              className="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full"
              alt={conference.data.Location + ' cityscape'}
            /> */}
            <img
              src={conference.data.Header_Image_Url}
              alt={conference.data.Conference_Name}
              className="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {conferenceStatus.early && !conferenceStatus.ended && !inPerson && (
        <WatchLiveAnnouncement
          conference={conference}
          conferenceLocation={conferenceLocation}
          startTime={startTime}
          stageDays={stageDays}
        />
      )}

      {/* {!conferenceStatus.started && (
        <div className="bg-white sm:pb-10">
          <div className="bg-gray-100 bg-hero-texture-slate pb-8 pt-8">
            <div className="max-w-screen-xl mx-auto sm:px-6 lg:px-8">
              <dl className="grid grid-cols-2 md:grid-cols-4">
                <div className="flex flex-col border-gray-700 p-4 sm:p-6 sm:text-center sm:border-0 sm:odd:border-r md:border-r md:last:border-r-0 last:border-b-0">
                  <dt className="order-2 mt-2 text-lg leading-6 font-medium text-gray-500">
                    Case studies sharing challenges and benefits
                  </dt>
                  <dd className="text-community-600 order-1 text-3xl leading-none font-extrabold">
                    Get inspired
                  </dd>
                </div>
                <div className="flex flex-col border-gray-700 p-4 sm:p-6 sm:text-center sm:border-0 sm:odd:border-r md:border-r md:last:border-r-0 last:border-b-0">
                  <dt className="order-2 mt-2 text-lg leading-6 font-medium text-gray-500">
                    Product | UX | Design | Dev | Team Enablement
                  </dt>
                  <dd className="text-community-600 order-1 text-3xl leading-none font-extrabold">
                    Latest practices
                  </dd>
                </div>
                <div className="flex flex-col border-gray-700 p-4 sm:p-6 sm:text-center sm:border-0 sm:odd:border-r md:border-r md:last:border-r-0 last:border-b-0">
                  <dt className="order-2 mt-2 text-lg leading-6 font-medium text-gray-500">
                    It takes a team to build an awesome product
                  </dt>
                  <dd className="text-community-600 order-1 text-3xl leading-none font-extrabold">
                    Break down silos
                  </dd>
                </div>
                <div className="flex flex-col border-gray-700 p-4 sm:p-6 sm:text-center sm:border-0 sm:odd:border-r md:border-r md:last:border-r-0 last:border-b-0">
                  <dt className="order-2 mt-2 text-lg leading-6 font-medium text-gray-500">
                    Grow your network of amazing product experts
                  </dt>
                  <dd className="text-community-600 order-1 text-3xl leading-none font-extrabold">
                    Meet your peers
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}

      {!conferenceStatus.started && (
        <section className="mt-16">
          <h3 className="mt-2 pb-4 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl sm:leading-10 text-center mb-8">
            There is <span className="text-community-700">a better way</span> of building products
          </h3>
          <div className="flex flex-wrap lg:flex-nowrap justify-center">
            <div className="border-community-700 rounded-lg mb-8 mx-4 sm:mx-8 lg:mr-2 lg:ml-4 xl:mr-4 xl:ml-8 border max-w-xs xl:max-w-md">
              <div className="h-full">
                <p className="bg-community-600 rounded-t-lg text-3xl uppercase text-center p-2 text-white font-semibold tracking-wider">
                  Effectiveness
                </p>
                <div className="grid grid-cols-2">
                  <div className="bg-community-100 border-community-700 grid grid-rows-131 p-4 xl:p-6 border-r rounded-bl-lg">
                    <img
                      className="w-full pr-4 xl:pr-8 h-16"
                      src={EffectivenessProject}
                      alt="Unhappy face because of invalid assumptions of value"
                    />
                    <p className="pt-8 pb-4 text-lg">Assuming value up front when we know the least</p>
                    <p className="pt-4 text-2xl sm:text-3xl font-mono uppercase tracking-normal">Project</p>
                  </div>
                  <div className="grid grid-rows-131 p-4 xl:p-6">
                    <img
                      className="w-full pr-4 xl:pr-8 h-16"
                      src={EffectivenessProduct}
                      alt="Happy face because of continuous validation"
                    />
                    <p className="pt-8 pb-4 text-lg font-bold">
                      Continuous questioning of assumptions and validation of value
                    </p>
                    <p className="pt-4 text-2xl sm:text-3xl font-mono uppercase font-bold tracking-wider">
                      Product
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-community-700 rounded-lg mb-8 mx-4 sm:mx-8 lg:mx-4 border max-w-xs xl:max-w-md">
              <div>
                <p className="bg-community-600 rounded-t-lg text-3xl uppercase text-center p-2 text-white font-semibold tracking-wider">
                  Efficiency
                </p>
                <div className="grid grid-cols-2">
                  <div className="bg-community-100 border-community-700 grid grid-rows-131 p-4 xl:p-6 border-r rounded-bl-lg">
                    <img
                      className="w-full pr-4 xl:pr-8 h-16"
                      src={EfficiencyProject}
                      alt="Projects assume value upfront, which is often wrong, and they prevent people learning while they are building the product"
                    />
                    <p className="pt-8 pb-4 text-lg">Separate specialities with functional goals</p>
                    <p className="pt-4 text-2xl sm:text-3xl font-mono uppercase tracking-normal">Project</p>
                  </div>
                  <div className="grid grid-rows-131 p-4 xl:p-6">
                    <img
                      className="w-full pr-4 xl:pr-8 h-16"
                      src={EfficiencyProduct}
                      alt="Products are more effective because they encourage accountability for outcomes"
                    />
                    <p className="pt-8 pb-4 text-lg font-bold">
                      Cross functional team with delivery as the goal
                    </p>
                    <p className="pt-4 text-2xl sm:text-3xl font-mono uppercase font-bold tracking-wider">
                      Product
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-community-700 rounded-lg mb-8 mx-4 sm:mx-8 lg:mr-4 lg:ml-2 xl:mr-8 xl:ml-4 border max-w-xs xl:max-w-md">
              <div>
                <p className="bg-community-600 rounded-t-lg text-3xl uppercase text-center p-2 text-white font-semibold tracking-wider">
                  Sustainability
                </p>
                <div className="grid grid-cols-2">
                  <div className="bg-community-100 border-community-700 grid grid-rows-131 p-4 xl:p-6 border-r rounded-bl-lg">
                    <img
                      className="w-full pr-4 xl:pr-8 h-16"
                      src={SustainabilityProject}
                      alt="Projects assume value upfront, which is often wrong, and they prevent people learning while they are building the product"
                    />
                    <p className="pt-8 pb-4 text-lg">Standardised team building predefined outputs</p>
                    <p className="pt-4 text-2xl sm:text-3xl font-mono uppercase tracking-normal">Project</p>
                  </div>
                  <div className="grid grid-rows-131 p-4 xl:p-6">
                    <img
                      className="w-full pr-4 xl:pr-8 h-16"
                      src={SustainabilityProduct}
                      alt="Products are more effective because they encourage accountability for outcomes"
                    />
                    <p className="pt-8 pb-4 text-lg font-bold">
                      Empowered teams using context to solve problems
                    </p>
                    <p className="pt-4 text-2xl sm:text-3xl font-mono uppercase font-bold tracking-wider">
                      Product
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )} */}

      {/* {isLoaded && !conferenceStatus.started && shuffledSpeakers && shuffledSpeakers.length > 0 && (
        <section>
          <div className="py-12 bg-white" id="speakers">
            <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="lg:text-center">
                <h3 className="mt-2 pb-4 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl sm:leading-10">
                  Our <span className="text-community-700">speakers</span>
                </h3>
              </div>
              <SpeakersList speakers={shuffledSpeakers} />
            </div>
          </div>
        </section>
      )}

      {!conferenceStatus.started && (
        <NewsletterSignUpSection
          conference={conference}
          conferenceLocation={conferenceLocation}
          options={[
            {
              name: 'emea',
              title: 'EMEA Announcements and Special Offers',
              default: true,
            },
            {
              name: 'apac',
              title: 'APAC Announcements and Special Offers',
              default: true,
            },
            {
              name: 'usa',
              title: 'USA Announcements & Special Offers',
              default: true,
            },
            {
              name: 'newsletter',
              title: 'I agree to receive emails from UXDX',
            },
          ]}
        />
      )} */}

      <section className="max-w-screen-xl mx-auto pt-8" id="agenda">
        <AgendaTemplate
          sessions={data.sessions.nodes}
          conferenceId={conference.data.Conference_Id}
          conferenceLocation={conferenceLocation}
          conferenceStartDate={conference.data.Conference_Start_Date}
          conferenceVenueType={conference.data.Conference_Venue_Type}
          conferenceTimezone={conference.data.Timezone}
          sort
        />
      </section>

      {!conference.data.External_Hosting_Site && onDisplayTickets.length > 0 && (
        <section id="tickets" className="max-w-screen-xl mx-auto pt-8">
          <h2 className="text-center md:text-left text-4xl font-extrabold text-gray-900">Tickets</h2>
          <Tickets
            allTickets={data.tickets2}
            onDisplayTickets={onDisplayTickets}
            conference={conference}
            hideDiscountedTickets
            externalTickets={conference.data.External_Hosting_Site}
          />
        </section>
      )}
      {conference.data.External_Hosting_Site && (
        <section id="tickets" className="max-w-screen-xl mx-auto pt-8">
          <div className="bg-blue-50 rounded-lg">
            <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
              <div className="mx-auto max-w-2xl text-center">
                <h2 className="text-balance text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                  Get your free ticket
                </h2>
                <p className="mx-auto mt-6 max-w-xl text-pretty text-lg/8 text-gray-600">
                  Before they run out.
                </p>
                <div className="mt-10 flex items-center justify-center gap-x-6">
                  <a
                    href={conference.data.External_Hosting_Site}
                    target="_blank"
                    className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    rel="noreferrer"
                  >
                    Register Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="max-w-screen-xl mx-auto pt-8 pb-16 px-8" id="partners">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-screen-xl mx-auto pt-8">
          {partners && partners.length > 0 && conference.data.Days_Away > -30 && (
            <div>
              <div className="">
                <h3 className="mt-2 pb-4 text-5xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl sm:leading-10">
                  Our <span className="text-community-700">partners</span>
                </h3>
              </div>
              <PartnersList
                partners={partners}
                conferenceLink={getEventLink(conference.data.Conference_Id, conferenceLocation)}
              />
            </div>
          )}
          {conference.data.Community_Leader && (
            <CommunityLeaders leaders={conference.data.Community_Leader} />
          )}
        </div>
      </div>

      {conferenceStatus.started && (
        <NewsletterSignUpSection
          conference={conference}
          conferenceLocation={conferenceLocation}
          options={[
            {
              name: 'emea',
              title: 'EMEA Announcements and Special Offers',
              default: true,
            },
            {
              name: 'apac',
              title: 'APAC Announcements and Special Offers',
              default: true,
            },
            {
              name: 'usa',
              title: 'USA Announcements & Special Offers',
              default: true,
            },
            {
              name: 'newsletter',
              title: 'I agree to receive emails from UXDX',
            },
          ]}
        />
      )}

      <section className="bg-slate-50">
        <div className="py-12">
          <div className="max-w-xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-16">Connect with other attendees</h2>
            <dl className="space-y-10 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-8">
              <div>
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-community-500 text-white">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                    />
                  </svg>
                </div>
                <div className="mt-5">
                  <dt className="text-lg leading-6 font-medium text-gray-900">
                    Take part in the scheduled sessions
                  </dt>
                  <dd className="mt-2 text-base text-gray-500">
                    Have some fun and meet some of the amazing Product, UX, Design and Dev experts that have
                    come together for UXDX from around the world.
                    <div className="mt-6 block pt-4">
                      <a
                        href="#agenda"
                        className="inline-flex px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-community-600 hover:bg-community-700"
                      >
                        View Agenda
                      </a>
                    </div>
                  </dd>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-community-500 text-white">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                    />
                  </svg>
                </div>
                <div className="mt-5">
                  <dt className="text-lg leading-6 font-medium text-gray-900">Get to know a Peer</dt>
                  <dd className="mt-2 text-base text-gray-500">
                    Have a chat with a fellow UXDX attendee - 2 minutes and a product development related
                    question to skip the small talk and create a real connection.
                    <div className="mt-6 block pt-4">
                      <a
                        href="https://speakeasy.co/uxdx"
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-community-600 hover:bg-community-700"
                      >
                        Start Chatting
                      </a>
                    </div>
                  </dd>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-community-500 text-white">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="m8.843 12.651c-1.392 0-2.521 1.129-2.521 2.521v6.306c0 1.392 1.129 2.521 2.521 2.521s2.521-1.129 2.521-2.521v-6.306c-.001-1.392-1.13-2.521-2.521-2.521z"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="m.019 15.172c0 1.393 1.13 2.523 2.523 2.523s2.523-1.13 2.523-2.523v-2.523h-2.521c-.001 0-.001 0-.002 0-1.393 0-2.523 1.13-2.523 2.523z"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="m8.846-.001c-.001 0-.002 0-.003 0-1.393 0-2.523 1.13-2.523 2.523s1.13 2.523 2.523 2.523h2.521c0-1.431 0-1.083 0-2.528-.001-1.391-1.128-2.518-2.518-2.518z"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="m2.525 11.37h6.318c1.393 0 2.523-1.13 2.523-2.523s-1.13-2.523-2.523-2.523h-6.318c-1.393 0-2.523 1.13-2.523 2.523s1.13 2.523 2.523 2.523z"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="m21.457 6.323c-1.391 0-2.518 1.127-2.518 2.518v.005 2.523h2.521c1.393 0 2.523-1.13 2.523-2.523s-1.13-2.523-2.523-2.523c-.001 0-.002 0-.003 0z"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="m12.641 2.522v6.325c0 1.392 1.129 2.521 2.521 2.521s2.521-1.129 2.521-2.521v-6.325c0-1.392-1.129-2.521-2.521-2.521-1.392 0-2.521 1.129-2.521 2.521z"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="m17.682 21.476c0-1.392-1.129-2.521-2.521-2.521h-2.521v2.523c.001 1.391 1.129 2.519 2.521 2.519s2.521-1.129 2.521-2.521z"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="m21.479 12.649h-6.318c-1.393 0-2.523 1.13-2.523 2.523s1.13 2.523 2.523 2.523h6.318c1.393 0 2.523-1.13 2.523-2.523s-1.13-2.523-2.523-2.523z"
                    />
                  </svg>
                </div>
                <div className="mt-5">
                  <dt className="text-lg leading-6 font-medium text-gray-900">
                    Keep the conversations going
                  </dt>
                  <dd className="mt-2 text-base text-gray-500">
                    Connect with the people you met, read about the latest trends in product development and
                    build your profile in the community
                    <div className="mt-6 block pt-4">
                      <a
                        href="https://join.slack.com/t/uxdxcommunity/shared_invite/zt-35pa4p852-BqMGL8z7G3CTP~pd7HqxSw"
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-community-600 hover:bg-community-700"
                      >
                        Join Slack
                      </a>
                    </div>
                  </dd>
                </div>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {schemaData.eventAttendanceMode !== 'Offline' && (
        <section className="max-w-screen-xl mx-auto pt-8 px-4 sm:px-6 lg:px-8" id="venue">
          <h2 className="text-4xl leading-snug font-extrabold text-gray-900 pb-4 border-b-2 border-gray-200">
            Venue
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 pt-4">
            <div className="flex flex-col justify-between">
              <div className="">
                <div className="grid lg:grid-cols-3 gap-4 pb-4 ">
                  <h3 className="text-2xl lg:col-span-2 break-word">{venue.data.Venue_Name}</h3>
                  {venue.data.Venue_Sponsor &&
                    venue.data.Venue_Sponsor[0] &&
                    venue.data.Venue_Sponsor[0].data.Company_Logo_Url &&
                    venue.data.Venue_Sponsor[0].data.Company_Url && (
                      <a
                        href={venue.data.Venue_Sponsor[0].data.Company_Url}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        <img
                          src={venue.data.Venue_Sponsor[0].data.Company_Logo_Url}
                          alt={venue.data.Venue_Sponsor[0].data.Company_Name}
                          className="object-contain max-h-10 mr-auto"
                        />
                      </a>
                    )}
                </div>
                <div
                  className="markdown"
                  dangerouslySetInnerHTML={{
                    __html: md.render(venue.data.Venue_Address),
                  }}
                />
                <div
                  className="pt-2 overflow-ellipsis overflow-hidden h-56 markdown"
                  dangerouslySetInnerHTML={{
                    __html: md.render(venue.data.Venue_Description),
                  }}
                />
              </div>
            </div>

            <div className="lg:col-span-2 h-96 lg:pl-8 pt-4 lg:pt-0 pb-8">
              {venue.data.Venue_Latitude && (
                <GoogleMapReact
                  bootstrapURLKeys={{
                    key: 'AIzaSyAiSaCasZD3KFwfMSH7fKm25JXge2CBbOY',
                  }}
                  defaultCenter={mapCenter}
                  defaultZoom={15}
                  onGoogleApiLoaded={({ map, maps }) =>
                    renderMarkers(map, maps, mapCenter, venue.data.Venue_Name)
                  }
                ></GoogleMapReact>
              )}
            </div>
          </div>
        </section>
      )}

      <div className="bg-white">
        <div className="max-w-7xl mx-auto py-16 px-4 divide-y-2 divide-gray-200 sm:py-24 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-extrabold text-gray-900">Frequently asked questions</h2>

          <div className="mt-6 pt-10">
            <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:grid-rows-2 md:gap-x-8 md:gap-y-12">
              {data.faqs.nodes.map((faq) => {
                // if the faq Delivery_Type does not contain any of the values in the conference.data.Conference_Venue_Type array then skip it
                if (
                  conference.data.Conference_Venue_Type &&
                  conference.data.Conference_Venue_Type.length > 0 &&
                  !conference.data.Conference_Venue_Type.some((r) => faq.data.Delivery_Type.includes(r))
                )
                  return null

                return (
                  <div key={faq.id}>
                    <dt className="text-lg leading-6 font-medium text-gray-900">{faq.data.Question}</dt>
                    <dd className="mt-2 text-base text-gray-500">
                      <div
                        className="markdown"
                        dangerouslySetInnerHTML={{
                          __html: md.render(faq.data.Answer),
                        }}
                      />
                    </dd>
                  </div>
                )
              })}
            </dl>
          </div>
        </div>
      </div>

      {/* <section>
        <div className='bg-white'>
          <div className='max-w-screen-xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8'>
            <p className='text-center text-base leading-6 font-semibold uppercase text-gray-600 tracking-wider'>
              Trusted by over 5,000 startups, scaleups and enterprises around the world
            </p>
            <CompanyLogoCloud companies={data.companies.nodes} />
          </div>
        </div>
      </section> */}

      <Testimonials conferenceLocation={conferenceLocation} />
    </Layout>
  )
}

export const postQuery = graphql`
  query ($id: String!, $confId: String!, $confRecordId: String!) {
    conference: airtableConference(id: { eq: $id }) {
      id
      data {
        Conference_Id
        Conference_Name
        Conference_Start_Date
        Conference_End_Date
        Conference_Venue_Type
        Physical_Capacity
        VenueCity: Venue_City__from_Venue_
        ProductTaxCountry: Country__from_Venue_
        Live_Start_Time
        Location
        Region
        Timezone
        Currency
        Year
        Type
        Days_Away
        Sales_Blurb
        Tax_Rate
        Organisation
        External_Hosting_Site
        Meta_Community_Url
        Manual_Conference_Card_Url
        Stats {
          id
          data {
            Stat
            Value
          }
        }
        Venue {
          data {
            Venue_Name
            Venue_City
            Venue_Type
            Venue_Description
            Venue_Address
            Venue_Sponsor {
              data {
                Company_Name
                Company_Url
                Company_Twitter
                Company_Logo_Url
              }
            }
            Venue_Latitude
            Venue_Longitude
            Country
            Post_Code
          }
        }
        Header_Image_Url
        Community_Leader {
          id
          data {
            Person_First
            Person_Last
            Person_Job_Title
            Person_Url_Slug
            Person_Bio
            Profile_Picture_Url_50
            Profile_Picture_Url_250
            Person_Company {
              data {
                Company_Name
                Company_Logo_Url
              }
            }
          }
        }
        Partners {
          id
          data {
            Company_Name
            Company_Url
            Company_Url_Slug
            Company_Logo_Url
            Company_Priority
            Company_Banner {
              localFiles {
                childImageSharp {
                  gatsbyImageData(quality: 80, layout: FULL_WIDTH, formats: [AUTO, WEBP])
                }
              }
            }
          }
        }
        Stage_Days {
          id
          data {
            Stage {
              data {
                Stage_Slug
              }
            }
            UTC_Start_Time
            UTC_End_Time
            Conference {
              data {
                Conference_Id
              }
            }
            LiveStream
            Zoom
          }
        }
      }
    }
    tickets2: allAirtableTickets2(
      filter: { data: { Conference_Id: { eq: $confId }, Status_Rollup__from_Prices_: { eq: "ON SALE" } } }
      sort: { data: { Priority: ASC } }
    ) {
      nodes {
        id
        recordId
        data {
          Name
          Ticket_SubTitle
          Priority
          Ticket_Includes
          Is_Upgrade
          Display_Button_Text
          Sale_Button_Text
          Delivery
          Access
          Ticket_Type_Name
          Stripe_Tax_Id
          Stripe_Tax_Id__Test_
          No_Tax
          Included_Workshops
          UXDX_Product_Id
          Product_Start_Date
          Product_Name
          Product_Type
          SendinBlue_Ticket_Id
          SendinBlue_Id
          Prices {
            data {
              Ticket_Time_Period
              UTC_Display_Time
              UTC_Start_Time
              UTC_End_Time
              Start_Time
              Stripe_Price_Id
              Stripe_Price_Id__Test_
              Stripe_Reduced_Price_Id
              Stripe_Reduced_Price_Id__Test_
              Price
              Currency: Currency__from_Ticket_
              Meta_Image_Url
            }
          }
          Tax_Rates {
            data {
              Stripe_Tax_Id
              Stripe_Tax_Id__Test_
              Rate
              Name
            }
          }
        }
      }
    }
    speakers: allAirtablePosition(
      filter: {
        data: {
          Conference: { eq: $confRecordId }
          Person_Status__from_Person_: { eq: "Published" }
          Hide_Speaker: { ne: "true" }
        }
      }
      sort: { data: { Person_Priority: ASC } }
    ) {
      nodes {
        id
        data {
          Person_Job_Title
          Person {
            id
            data {
              Person_First
              Person_Last
              Person_Twitter
              Person_Status
              Hide_Speaker
              Profile_Picture_Url_50
              Profile_Picture_Url_250
              Person_Url_Slug
              Person_Priority
            }
          }
          Company {
            data {
              Company_Name
              Company_Logo_Url
            }
          }
          Sessions {
            id
            data {
              Conference {
                data {
                  Conference_Id
                }
              }
              Session_Status
            }
          }
        }
      }
    }
    sessions: allAirtableSessions(
      filter: {
        data: { Conference: { elemMatch: { id: { eq: $id } } }, Session_Status: { eq: "Published" } }
      }
      sort: { data: { UTC_Start_Time: ASC } }
    ) {
      nodes {
        id
        recordId
        data {
          Talk_Title
          Talk_Description
          UTC_Start_Time
          UTC_End_Time
          Session_Status
          Session_Slug
          Public_Release_Date
          Calendar_Id
          Slideshare
          Video
          Mux_Video
          Capacity
          Conference {
            data {
              Conference_Id
              Conference_Name
              Year
              Location
            }
          }
          Speakers {
            id
            data {
              Person_Job_Title
              Person {
                id
                data {
                  Person_First
                  Person_Last
                  Person_Twitter
                  Person_Url_Slug
                  Person_Status
                  Person_Linkedin
                  Hide_Speaker
                  Person_Priority
                  Profile_Picture_Url_50
                  Profile_Picture_Url_250
                }
              }
              Company {
                data {
                  Company_Name
                  Company_Logo_Url
                }
              }
            }
          }
          Session_Groups {
            data {
              Group
              Requires_Register
              Recordings_Available
              Requires_Stage
              Hide_Main_Agenda
              Job
              Delivery
              Format
            }
          }
          Stream {
            data {
              Stream_Name
            }
          }
          Stage {
            data {
              Stage_Name
              Stage_Colour
              Stage_Priority
              Stage_Slug
            }
          }
        }
      }
    }
    companies: allAirtableCompanies(filter: { data: { Highlight: { eq: "Home" } } }) {
      nodes {
        data {
          Company_Name
          Company_Logo_Url
        }
      }
    }
    site {
      siteMetadata {
        url
      }
    }
    faqs: allAirtableFaQs(filter: { data: { Page: { eq: "Community" } } }, sort: { data: { Order: ASC } }) {
      nodes {
        id
        data {
          Answer
          Order
          Question
          Delivery_Type
        }
      }
    }
  }
`

CommunityPage.propTypes = {
  data: PropTypes.object.isRequired,
}
export default CommunityPage
